import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { SKIP_DELEGATION_CONTEXT } from '../http/skip-delegation-context.token';
import { AuthService } from '../services/auth.service';

const DELEGATION_HEADER_NAME = 'X-Delegated-Owner-Id';
const SUPPORT_SESSION_HEADER_NAME = 'X-Support-Session-Id';
const ADMIN_DASHBOARD_PREVIEW_HEADER = 'X-Admin-Dashboard-Preview';

const SUPPORTED_PATH_PREFIXES = [
  '/me/auto-messages',
  '/me/twitch/moderation-settings',
  '/me/twitch/schedule',
  '/me/twitch/streams',
  '/me/twitch/clips',
  '/me/twitch/channel',
  '/me/twitch/users',
  '/me/twitch/moderators',
  '/me/twitch/vips',
  '/me/twitch/editors',
  '/me/twitch/chat-badges',
  '/me/twitch/command-variables',
  '/me/twitch/scopes',
  '/me/schedule-discord',
  '/api/commands',
  '/api/command-definitions',
  '/api/default-command-settings',
  '/api/irc/channels'
];

function getPathname(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname.toLowerCase();
  } catch {
    return (url ?? '').toLowerCase();
  }
}

function supportsDelegation(url: string, auth: AuthService): boolean {
  const pathname = getPathname(url);
  if (auth.isAdminDashboardPreview) {
    return pathname.startsWith('/me') || pathname.startsWith('/api/');
  }
  return SUPPORTED_PATH_PREFIXES.some(
    prefix => pathname === prefix
      || pathname.startsWith(`${prefix}/`)
      || pathname.endsWith(prefix)
      || pathname.includes(`${prefix}/`)
  );
}

export const delegationContextInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  if (req.context.get(SKIP_DELEGATION_CONTEXT)) {
    return next(req);
  }
  const activeDelegation = auth.activeDelegation;
  if (!activeDelegation?.ownerUserId) {
    return next(req);
  }
  if (req.headers.has(DELEGATION_HEADER_NAME) || !supportsDelegation(req.url, auth)) {
    return next(req);
  }
  return next(req.clone({
    setHeaders: {
      [DELEGATION_HEADER_NAME]: activeDelegation.ownerUserId,
      ...(activeDelegation.supportMode === 'support' && activeDelegation.supportSessionId
        ? { [SUPPORT_SESSION_HEADER_NAME]: activeDelegation.supportSessionId }
        : {}),
      ...(activeDelegation.supportMode === 'admin_preview'
        ? { [ADMIN_DASHBOARD_PREVIEW_HEADER]: 'true' }
        : {})
    }
  }));
};

